import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'

import { allowedRoutesWithoutAuth } from '../config/allowedRoutes'

// Replicate axios 0.24's percent-encoding (leaves `: $ , [ ]` readable, spaces as `+`).
function encodeParam(value: string): string {
  return encodeURIComponent(value)
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')
    .replace(/%5B/gi, '[')
    .replace(/%5D/gi, ']')
}

// axios 1.x serializes nested objects as `date[start]=...`, but the backend reads
// `date` as a JSON string (`date={"start":...}`). Keep 0.24's serialization so the
// request contract survives the version bump: objects -> JSON, arrays -> `key[]=`.
function serializeParams(params: Record<string, unknown>): string {
  // URLSearchParams carries its own encoding — keep 0.24's passthrough.
  if (params instanceof URLSearchParams) {
    return params.toString()
  }

  const parts: string[] = []

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return
    }

    let listKey = key
    let values: unknown[]
    if (Array.isArray(value)) {
      listKey = `${key}[]`
      values = value
    } else {
      values = [value]
    }

    values.forEach((item) => {
      let encoded: string
      if (item instanceof Date) {
        encoded = item.toISOString()
      } else if (item !== null && typeof item === 'object') {
        encoded = JSON.stringify(item)
      } else {
        encoded = String(item)
      }

      parts.push(`${encodeParam(listKey)}=${encodeParam(encoded)}`)
    })
  })

  return parts.join('&')
}

// O 429 do Laravel vem do middleware `throttle`, que responde ANTES de chegar ao
// controller — a request não teve efeito nenhum, então repeti-la não duplica nada
// (não é o caso de um 500 ou de um timeout, onde o servidor pode ter processado).
// Por isso o retry aqui é seguro até em POST/PUT.
const RATE_LIMIT_MAX_RETRIES = 2
// O `Retry-After` do Laravel é o tempo até a janela zerar, e pode chegar a 60s.
// Esperar isso em silêncio parece travamento, então acima do teto o erro sobe para
// a página tratar. O retry é para suavizar rajada pequena, não para segurar a tela.
const RATE_LIMIT_MAX_WAIT_MS = 8000

interface IRetriableConfig extends InternalAxiosRequestConfig {
  retryCount?: number
}

function parseRetryAfter(header: unknown): number | null {
  if (typeof header !== 'string' || !header.trim()) {
    return null
  }

  const seconds = Number(header)
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000)
  }

  const date = Date.parse(header)
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now())
}

const configValue: string = process.env.REACT_APP_API_URL as string
const apiService = axios.create({
  baseURL: `${configValue}api/`,
  paramsSerializer: {
    serialize: serializeParams
  }
})

apiService.interceptors.response.use(undefined, async (error: AxiosError) => {
  const config = error.config as IRetriableConfig | undefined

  if (error.response?.status === 429 && config) {
    const retryCount = config.retryCount ?? 0
    // Sem `Retry-After` assume-se 1s: o balde do Laravel é por minuto, e uma rajada
    // que estourou por pouco costuma liberar no segundo seguinte.
    const wait = parseRetryAfter(error.response.headers?.['retry-after']) ?? 1000

    if (retryCount < RATE_LIMIT_MAX_RETRIES && wait <= RATE_LIMIT_MAX_WAIT_MS) {
      config.retryCount = retryCount + 1
      // Jitter para não realinhar: numa rajada, todas as requests levam 429 no mesmo
      // instante e voltariam juntas, estourando o limite de novo.
      const jitter = Math.random() * 500
      await new Promise((resolve) => setTimeout(resolve, wait + jitter))
      return apiService.request(config)
    }
  }

  // Rotas que não requerem autenticação
  const allowedRoutes = [...allowedRoutesWithoutAuth, /\/login$/]

  const isAllowedRoute = allowedRoutes.some((route) => route.test(window.location.pathname))

  if (error.response?.status === 401 && !isAllowedRoute) {
    // Preserve the current deep link (e.g. a kanban card link from an e-mail) so the
    // login page can return here after re-authenticating on an expired session.
    sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search)
    localStorage.clear()
    window.location.href = '/login'
  }

  return Promise.reject(error)
})

apiService.interceptors.request.use(async (config) => {
  const token = localStorage.getItem('token')

  if (token) {
    config.headers = config.headers || {}
    config.headers.Accept = 'application/json'
    config.headers.Authorization = `Bearer ${token}`

    // Só define Content-Type se não for FormData
    // (o axios define automaticamente para multipart/form-data quando necessário)
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json'
    }
  }

  return config
})

export default apiService
